/**
 * Packen und Kodieren der Kachel-Atlanten im Browser.
 */

import { ATLAS_CELL } from '../../shared/constants.ts';
import { atlasCols, atlasSize, tileCopyRect } from '../../shared/atlasLayout.ts';
import type { LosslessMime } from '../../shared/protocol.ts';

/**
 * Ermittelt zur Laufzeit, ob der Browser überhaupt WebP kodieren kann.
 *
 * Früher prüfte diese Stelle zusätzlich, ob WebP bei `quality: 1` wirklich
 * verlustfrei herauskommt – Chrome nimmt dort trotzdem den verlustbehafteten
 * Encoder. Seit auch der Text-Atlas mit TEXT_QUALITY komprimiert wird, ist
 * diese Unterscheidung gegenstandslos: Gebraucht wird WebP, weil nur dort die
 * Qualitätsstufe überhaupt wirkt. PNG bleibt der Notnagel für Browser ohne
 * WebP-Encoder – dort wird der Text-Atlas mangels Stufe verlustfrei.
 */
export async function detectTextFormat(): Promise<LosslessMime> {
  const canvas = new OffscreenCanvas(32, 32);
  // Ohne Kontext gilt das Canvas als nicht initialisiert und convertToBlob
  // wirft – der Probelauf liefe dann immer in den PNG-Zweig.
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'image/png';
  ctx.fillRect(0, 0, 32, 32);

  try {
    const blob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.8 });
    return blob.type === 'image/webp' ? 'image/webp' : 'image/png';
  } catch {
    return 'image/png';
  }
}

/** Packt Kacheln aus einem Quellcanvas dicht in einen Atlas und kodiert ihn. */
export class AtlasPacker {
  private canvas = new OffscreenCanvas(ATLAS_CELL, ATLAS_CELL);
  private ctx = this.canvas.getContext('2d')!;

  constructor(
    private source: CanvasImageSource,
    private width: number,
    private height: number,
    private tilesX: number,
  ) {}

  setSource(source: CanvasImageSource, width: number, height: number, tilesX: number): void {
    this.source = source;
    this.width = width;
    this.height = height;
    this.tilesX = tilesX;
  }

  async pack(tiles: number[], type: string, quality: number): Promise<Uint8Array | null> {
    if (tiles.length === 0) return null;

    const { width, height } = atlasSize(tiles.length);
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.ctx.clearRect(0, 0, width, height);

    const cols = atlasCols(tiles.length);
    for (let cell = 0; cell < tiles.length; cell++) {
      const rect = tileCopyRect(tiles[cell]!, cell, this.tilesX, cols, this.width, this.height);
      if (rect.width <= 0 || rect.height <= 0) continue;
      this.ctx.drawImage(
        this.source,
        rect.srcX,
        rect.srcY,
        rect.width,
        rect.height,
        rect.destX,
        rect.destY,
        rect.width,
        rect.height,
      );
    }

    const blob = await this.canvas.convertToBlob({ type, quality });
    const data = new Uint8Array(await blob.arrayBuffer());
    // Ein leerer Atlas käme beim Server als "Bild vorhanden, aber unlesbar" an
    // und kostete ihn ein Vollbild. Lieber hier auffallen.
    if (data.length === 0) throw new Error(`Kodierung lieferte 0 Byte (${type}, ${tiles.length} Kacheln)`);
    return data;
  }
}
